import type { InputType } from 'config'
import { IMailPostage, IMailRateCard, IPrintMailConfig } from 'pages/Servicing/PrintMail/types'

export const mailTypeOptions: Record<string, string> = {
  usps_first_class: 'USPS First Class',
  usps_standard: 'USPS Standard (cheaper, slower, no extra service)',
}

export const extraServiceOptions: Record<string, string> = {
  '': 'None',
  certified: 'Certified',
  certified_return_receipt: 'Certified with return receipt',
  registered: 'Registered',
}

/** Matches `defaultPrintMailPostage` on the backend. */
export const defaultMailPostage = (): IMailPostage => ({
  color: false,
  doubleSided: true,
  mailType: 'usps_first_class',
  extraService: '',
  returnEnvelope: false,
  perforatedPage: 1,
})

/**
 * Lob's published prices at the lowest volume tier, which is what a fresh tenant sees until they
 * put their own negotiated numbers in. Matches `defaultPrintMailRateCard` on the backend.
 */
export const defaultMailRateCard = (): IMailRateCard => ({
  basePieceCents: 82,
  perPageCents: 10,
  colorPerPageCents: 10,
  certifiedCents: 680,
  certifiedReturnReceiptCents: 900,
  registeredCents: 2000,
  returnEnvelopeCents: 23,
})

export const defaultPrintMailConfig = (): IPrintMailConfig => ({
  enabled: false,
  provider: 'lob',
  fromName: '',
  fromAddressLine1: '',
  fromAddressLine2: '',
  fromCity: '',
  fromState: '',
  fromZip: '',
  postage: defaultMailPostage(),
  purposes: {},
  rateCard: defaultMailRateCard(),
  requireDeliverableAddress: true,
  allowMissingUnit: true,
  autoDispatch: true,
  dispatchBatchSize: 25,
  dailyPieceCap: 0,
})

/**
 * The scalar settings, rendered through `RenderInput`.
 *
 * `providerOptions` comes from the server's own registry (`GET /print-mail/config` returns the
 * providers this build carries), so the search index can call this with none and still read every
 * title.
 */
export const settingInputs = (providerOptions: Record<string, string> = { lob: 'Lob' }): Record<string, InputType> => ({
  serviceSection: {
    title: 'Service',
    inputType: 'section',
    span: 2,
  },
  enabled: {
    title: 'Print and post physical mail',
    inputType: 'toggle',
    required: false,
    tooltip:
      'Off means nothing can be queued at all, whatever the provider credential says. Having a key saved and being ' +
      'willing to spend postage out of it are two different decisions.',
  },
  provider: {
    title: 'Provider',
    inputType: 'select',
    options: providerOptions,
    hasDefaultOption: false,
    required: false,
    tooltip: 'The vendor the letters go to. The credential itself lives under Admin Tools > Vendors.',
  },
  autoDispatch: {
    title: 'Post queued pieces automatically',
    inputType: 'toggle',
    required: false,
    tooltip:
      'On, a dispatcher tick submits what is queued every minute. Off, the queue waits for somebody to press ' +
      '"Post the queue now" on the Print & Mail screen, so spending postage is a deliberate act by a named person.',
  },
  dispatchBatchSize: {
    title: 'Pieces per dispatch',
    inputType: 'text',
    type: 'number',
    required: false,
    tooltip: 'How many pieces one dispatcher tick submits. 1 to 200.',
  },
  dailyPieceCap: {
    title: 'Daily piece cap',
    inputType: 'text',
    type: 'number',
    required: false,
    tooltip:
      'The most pieces that may be posted in one day; 0 for no limit. A run over a 50,000 loan book is one ' +
      'mistaken filter away from being the whole book, and reaching the cap holds the rest rather than failing ' +
      'them - so nothing is lost, and nothing more is spent.',
  },

  addressSection: {
    title: 'Return address',
    inputType: 'section',
    span: 2,
  },
  fromName: {
    title: 'Return name',
    inputType: 'text',
    required: false,
    tooltip:
      'Leave the whole block blank to use the company contact block from Configuration > Footer, which is the ' +
      'address already printed on your statements. Fill it in when mail should come back somewhere else, such as ' +
      'a lockbox rather than the office.',
  },
  fromAddressLine1: { title: 'Address line 1', inputType: 'text', required: false },
  fromAddressLine2: { title: 'Address line 2', inputType: 'text', required: false },
  fromCity: { title: 'City', inputType: 'text', required: false },
  fromState: { title: 'State', inputType: 'text', required: false },
  fromZip: { title: 'Zip', inputType: 'text', required: false },

  addressQualitySection: {
    title: 'Address quality',
    inputType: 'section',
    span: 2,
  },
  requireDeliverableAddress: {
    title: 'Hold a piece the carrier will not vouch for',
    inputType: 'toggle',
    required: false,
    tooltip:
      'On, an undeliverable address holds the piece instead of posting it. The alternative is paying to print and ' +
      'post a letter USPS has already said it cannot deliver, and then not knowing it never arrived. A held piece ' +
      'is released from the Print & Mail queue once the address is fixed.',
  },
  allowMissingUnit: {
    title: 'Treat a missing apartment number as deliverable',
    inputType: 'toggle',
    required: false,
    tooltip:
      'On, because a migrated servicing book is full of addresses missing a unit number that the carrier has been ' +
      'delivering to for years. A fully undeliverable address is held either way.',
  },
})

/** The tenant's default postage. A purpose may override any of these. */
export const postageInputs = (): Record<string, InputType> => ({
  mailType: {
    title: 'Mail class',
    inputType: 'select',
    options: mailTypeOptions,
    hasDefaultOption: false,
    required: false,
  },
  extraService: {
    title: 'Extra service',
    inputType: 'select',
    options: extraServiceOptions,
    hasDefaultOption: false,
    required: false,
    tooltip:
      'The difference between a statement and a demand letter: one is information, the other may have to be proved ' +
      'delivered years later. Only offered on First Class - the provider rejects it on Standard post.',
  },
  color: {
    title: 'Print in colour',
    inputType: 'toggle',
    required: false,
    tooltip: 'Off by default: it roughly doubles the per-page charge and a notice is black and white.',
  },
  doubleSided: {
    title: 'Print double sided',
    inputType: 'toggle',
    required: false,
    tooltip: 'Fewer sheets of paper. Providers still price per printed page, so the estimate does not halve.',
  },
  returnEnvelope: {
    title: 'Include a reply envelope',
    inputType: 'toggle',
    required: false,
    tooltip: 'Costs more per piece, and needs the perforated page below to say which page tears out.',
  },
  perforatedPage: {
    title: 'Perforated page',
    inputType: 'text',
    type: 'number',
    required: false,
    tooltip: 'Which page of the letter tears off as the reply slip. Only used with a reply envelope.',
  },
})

/**
 * What the tenant pays per piece.
 *
 * Every figure the platform shows is derived from these and is an ESTIMATE: no print-and-mail
 * provider returns a price on the create call, so the only authority on what was actually charged
 * is the provider's invoice. It exists because "this run will cost about $4,100" is the first thing
 * anyone authorising a portfolio-wide mailing asks, and the alternative to an estimate is no answer.
 */
export const rateCardInputs = (): Record<string, InputType> => ({
  basePieceCents: {
    title: 'Base piece (cents)',
    inputType: 'text',
    type: 'number',
    required: false,
    tooltip: 'A one page, black and white, First Class letter. Covers the first page.',
  },
  perPageCents: {
    title: 'Each extra page (cents)',
    inputType: 'text',
    type: 'number',
    required: false,
  },
  colorPerPageCents: {
    title: 'Colour, per page (cents)',
    inputType: 'text',
    type: 'number',
    required: false,
  },
  returnEnvelopeCents: {
    title: 'Reply envelope (cents)',
    inputType: 'text',
    type: 'number',
    required: false,
  },
  certifiedCents: {
    title: 'Certified (cents)',
    inputType: 'text',
    type: 'number',
    required: false,
  },
  certifiedReturnReceiptCents: {
    title: 'Certified with return receipt (cents)',
    inputType: 'text',
    type: 'number',
    required: false,
  },
  registeredCents: {
    title: 'Registered (cents)',
    inputType: 'text',
    type: 'number',
    required: false,
  },
})
